/**
 * THE REFERENCE ENGINE — precedent selection and the construction/identity fence.
 *
 * PURE, and it takes the registry as an ARGUMENT. Same discipline as
 * `assignmentEngine.js` + `data/rep_proficiency.json`: the data is read at
 * RUNTIME by the bound layer (`features/artwork/references.js`), the logic here
 * knows nothing about how it was loaded, and `verify-references.mjs` drives
 * these exact functions under plain Node with the real JSON — so the assertions
 * test the shipped module rather than a copy of it.
 *
 * WHY IT EXISTS. Measured 2026-09-09: neither Canva nor ClickUp gives a stable,
 * directly-addressable image URL.
 *   ClickUp attachment URL  ~5 minutes, single-use
 *   Canva media URL         signed, exp ~4.7 h (measured 16,938 s)
 * Every Canva URL captured earlier that session had expired within 51 minutes;
 * the IDs resolved fine throughout. **A URL is a fetch, never an address.** The
 * durable address is the reference_id plus (source_system, native id). This
 * module turns one into the other and hands back a FETCH PLAN. It performs NO
 * network I/O — the caller owns that.
 *
 * Ported from `ai-tools/ring-concept-gpt/resolver.mjs`, which stays as the
 * bench-side copy the GPT runbook documents. This is the one the app runs.
 */

export const CLICKUP_LIST = '901000496809'

/* ------------------------------------------------------------------ *
 * Native id parsing
 * ------------------------------------------------------------------ */

/**
 * Registry entries carry human-written fingerprints such as
 *   "task 868m2afn2 / attachment 99dedbf5-....png / render .921"
 * Parse DEFENSIVELY: a fingerprint that cannot be parsed must fail loudly
 * rather than resolve to something plausible-looking.
 */
export function parseNativeIds(entry) {
  if (!entry || typeof entry !== 'object') return null
  const fp = typeof entry.source_fingerprint_or_native_id === 'string'
    ? entry.source_fingerprint_or_native_id : ''
  const mirror = typeof entry.mirror_native_id === 'string' ? entry.mirror_native_id : ''

  const task = /\btask\s+([a-z0-9]+)/i.exec(fp)
  const attachment = /\battachment\s+([A-Za-z0-9._-]+)/i.exec(fp)
  const render = /\brender\s+(\.\d+)/i.exec(fp)
  const canva = /\b(?:Canva\s+asset\s+)?(MAH[A-Za-z0-9_-]{6,})\b/.exec(mirror || fp)

  return {
    clickupTaskId: task ? task[1] : null,
    clickupAttachmentId: attachment ? attachment[1] : null,
    renderId: render ? render[1] : null,
    canvaAssetId: canva ? canva[1] : null,
  }
}

/* ------------------------------------------------------------------ *
 * Staleness
 * ------------------------------------------------------------------ */

const DAY = 86400000

export function daysSinceVerified(entry, today = new Date()) {
  const raw = entry && entry.source_last_verified_at
  if (!raw) return null // never verified — NOT "0 days ago"
  const then = Date.parse(raw)
  if (Number.isNaN(then)) return null
  return Math.floor((today.getTime() - then) / DAY)
}

/**
 * A reference nobody has checked is not the same as one checked long ago, and
 * neither is the same as one checked yesterday. THREE states, never two.
 */
export function verificationState(entry, today = new Date(), maxAgeDays = 90) {
  const d = daysSinceVerified(entry, today)
  if (d === null) return 'NEVER_VERIFIED'
  return d > maxAgeDays ? 'STALE' : 'FRESH'
}

/** How the verification state reads on screen — three states, three sentences. */
export function verificationNote(state) {
  switch (state) {
    case 'FRESH': return 'Checked recently — the stored id resolved.'
    case 'STALE': return 'Not checked in over 90 days — the id may no longer resolve. Re-verify before relying on the file.'
    default: return 'Never verified. Nobody has confirmed this id resolves to anything.'
  }
}

/* ------------------------------------------------------------------ *
 * THE LICENSING FENCE
 * ------------------------------------------------------------------ */

/**
 * REUSE IS A PROPERTY OF WHAT YOU TAKE, NOT OF THE FILE.
 *
 * The first version of this guard put ONE flag over the whole asset and refused
 * Hurricanes / Padres / LSU wholesale. That contradicted how SR actually works:
 * designers open a prior job as a starting point — a rodeo task holds a hockey
 * `.3dm`, archery holds track & field, powerlifting holds Mercer tennis. A rule
 * that blocks cross-account precedent blocks the thing that makes SR fast.
 *
 *   construction / house look / technique  → OPEN across accounts. SR's craft.
 *   client identity                        → NEVER across accounts.
 *
 * A Hurricanes render teaching "this is how a rail bounds a pavé row" carries
 * nothing belonging to the Hurricanes. Identity arrives from the job's OWN
 * owned crest via the decal swap, never from a precedent.
 */
export const CONSTRUCTION = 'construction'
export const IDENTITY = 'identity'

export function reuseAllowed(entry, { purpose = CONSTRUCTION, sameAccount = false } = {}) {
  if (purpose !== CONSTRUCTION && purpose !== IDENTITY) {
    return { allowed: false, purpose, reason: `unknown purpose "${purpose}" — fail closed` }
  }
  if (purpose === CONSTRUCTION) {
    const rule = entry && entry.reuse && entry.reuse.construction
    if (rule && rule !== 'open') {
      return { allowed: false, purpose, reason: `construction reuse restricted on this reference (${rule})` }
    }
    return { allowed: true, purpose, reason: 'construction, house look and technique are SR craft — open across accounts' }
  }
  if (sameAccount) {
    return { allowed: true, purpose, reason: "a customer's own identity is reusable for that customer" }
  }
  return {
    allowed: false,
    purpose,
    reason: 'client identity never crosses accounts — take the construction and inject the job\'s own owned crest',
  }
}

/** What may and may not be taken from a reference, BY NAMED ASPECT. */
export function takeableFrom(entry, { sameAccount = false } = {}) {
  const teaches = Array.isArray(entry && entry.teaches) ? entry.teaches : []
  const mayTake = []
  const mustNotTake = []
  for (const t of teaches) {
    const cls = t && t.class === IDENTITY ? IDENTITY : CONSTRUCTION
    const verdict = reuseAllowed(entry, { purpose: cls, sameAccount })
    ;(verdict.allowed ? mayTake : mustNotTake).push(t.aspect)
  }
  return { mayTake, mustNotTake }
}

/* ------------------------------------------------------------------ *
 * resolveReference()
 * ------------------------------------------------------------------ */

export function resolveReference(registry, referenceId, opts = {}) {
  const refs = (registry && Array.isArray(registry.references)) ? registry.references : []
  const entry = refs.find((r) => r && r.reference_id === referenceId)
  if (!entry) return { ok: false, error: 'NOT_FOUND', referenceId }

  if (entry.active_status && entry.active_status !== 'active') {
    return {
      ok: false,
      error: 'NOT_ACTIVE',
      referenceId,
      activeStatus: entry.active_status,
      supersededBy: entry.supersedes_reference_id || null,
    }
  }
  if (entry.blocker) return { ok: false, error: 'BLOCKED', referenceId, blocker: entry.blocker }

  const ids = parseNativeIds(entry)
  const plan = fetchPlan(entry, ids)
  if (!plan) {
    return {
      ok: false,
      error: 'UNRESOLVABLE',
      referenceId,
      detail: 'no usable native id — the fingerprint could not be parsed',
    }
  }

  return {
    ok: true,
    referenceId,
    title: entry.title,
    sourceSystem: entry.source_system,
    ids,
    fetchPlan: plan,
    verification: verificationState(entry, opts.today || new Date(), opts.maxAgeDays ?? 90),
    lifecycleStage: entry.lifecycle_stage || 'UNKNOWN',
    classification: entry.classification || 'UNKNOWN',
    ...takeableFrom(entry, opts),
    committedDerivative: entry.committed_derivative || null,
    avoidPatterns: entry.avoid_patterns || null,
  }
}

/** The fetch plan NAMES the call the caller should make. This module never makes it. */
export function fetchPlan(entry, ids = parseNativeIds(entry)) {
  if (!ids) return null
  if (ids.clickupTaskId && ids.clickupAttachmentId) {
    return {
      via: 'clickup',
      call: 'clickup_download_task_attachment',
      args: { task_id: ids.clickupTaskId, attachment_id: ids.clickupAttachmentId },
      urlLifetime: '~5 minutes, single-use — fetch immediately and exactly once',
      yields: 'the full-resolution original',
    }
  }
  if (ids.canvaAssetId) {
    return {
      via: 'canva',
      call: 'get-assets',
      args: { asset_ids: [ids.canvaAssetId] },
      urlLifetime: '~4.7 hours, signed',
      yields: 'a thumbnail URL; NOT the full-resolution original',
    }
  }
  return null
}

/* ------------------------------------------------------------------ *
 * The house-look set and the per-job selector
 * ------------------------------------------------------------------ */

export function houseLookSet(registry) {
  const refs = (registry && Array.isArray(registry.references)) ? registry.references : []
  return refs.filter((r) => /^SR-REF-00000[1-6]$/.test((r && r.reference_id) || ''))
}

/**
 * The per-job selector. Scores on the registry's own fields and ALWAYS returns
 * why — a rep must be able to disagree with it, which is why `candidates` comes
 * back alongside the pick rather than only the winner.
 *
 * Construction precedent is OPEN across accounts — that is the point of the
 * corpus. A reference is never excluded here for being another customer's job;
 * the identity layer is fenced per-aspect by `takeableFrom()` instead.
 */
export function selectPerJob(registry, job = {}, opts = {}) {
  const refs = (registry && Array.isArray(registry.references)) ? registry.references : []
  const pool = refs.filter((r) => r && r.active_status === 'active' && !r.blocker)

  const scored = []
  for (const r of pool) {
    const same = sameAccount(r, job)
    let score = 0
    const why = []
    if (eq(r.sport_or_category, job.sport)) { score += 40; why.push('same sport') }
    if (eq(r.competition_or_product_level, job.level)) { score += 25; why.push('same level') }
    if (eq(r.asset_type, job.assetType)) { score += 10; why.push('same asset type') }
    if (job.metal && String(r.material_setting_notes || '').toLowerCase().includes(String(job.metal).toLowerCase())) {
      score += 15
      why.push('same metal')
    }
    if (score > 0) {
      scored.push({
        reference_id: r.reference_id,
        title: r.title,
        customer: r.customer_or_program || '',
        sameAccount: same,
        score,
        why,
        verification: verificationState(r, opts.today || new Date(), opts.maxAgeDays ?? 90),
        avoidPatterns: r.avoid_patterns || null,
        ...takeableFrom(r, { sameAccount: same }),
      })
    }
  }
  scored.sort((a, b) => b.score - a.score || a.reference_id.localeCompare(b.reference_id))

  if (!scored.length) {
    return {
      ok: false,
      rung: 'HOUSE_SET_ONLY',
      reason: 'nothing in the registry scored above zero for this job — attach the house set alone and say so',
      candidates: [],
    }
  }
  const top = scored[0]
  return {
    ok: true,
    rung: top.score >= 40 ? 'SAME_SPORT' : 'NEAREST_AVAILABLE',
    selected: top.reference_id,
    title: top.title,
    why: top.why,
    mayTake: top.mayTake,
    mustNotTake: top.mustNotTake,
    verification: top.verification,
    avoidPatterns: top.avoidPatterns,
    identityNote: 'Identity elements come from this job\'s own owned crest via the decal swap, never from the precedent.',
    candidates: scored.slice(0, (opts.maxCandidates ?? 3)),
  }
}

/** How a fallback rung reads on screen. A rung with no sentence is a code leak. */
export function rungNote(rung) {
  switch (rung) {
    case 'SAME_SPORT': return 'A precedent in this sport.'
    case 'NEAREST_AVAILABLE': return 'No precedent in this sport yet — this is the nearest the registry holds.'
    default: return 'No precedent scored for this job. The house look set is the only reference; say so rather than implying one was found.'
  }
}

function eq(a, b) {
  if (!a || !b) return false
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
}

/**
 * EXACT, and deliberately so.
 *
 * It is tempting to let "LSU" match "LSU football (print supplied by Brian)".
 * But this predicate is what UNLOCKS another customer's identity, and a loose
 * match fails in the dangerous direction: "Carolina" would match "Carolina
 * Hurricanes 2026 Stanley Cup" for a job that is Carolina Panthers. The same
 * collision class that produced four separate businesses named "Champion …" in
 * the cheer TAM. A false negative costs one identity aspect the operator did
 * not need anyway — the construction is open either way. A false positive is a
 * licensed mark on the wrong account's ring.
 */
function sameAccount(entry, job) {
  if (!job || !job.customer) return false
  return eq(entry.customer_or_program, job.customer)
}
